import { useState, useEffect, useCallback } from "react";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { useAuth } from "@/hooks/use-auth";
import { Redirect, Link } from "wouter";
import { useToast } from "@/hooks/use-toast";
import { Plus, Pencil, Trash2, CheckCircle, XCircle, Star, GitMerge } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";

import { useCategories } from "@/hooks/use-categories";
import { ImageUpload } from "@/components/ImageUpload";

const TOWNS = ["Ayr","Kilmarnock","Irvine","Prestwick","Troon","Saltcoats","Ardrossan","Largs","Kilwinning","Stewarton","Maybole","Girvan","Cumnock","Auchinleck","Newmilns","Kilbirnie","Beith","Dalry"];

function authHeaders() {
  const token = localStorage.getItem("ayrshire_token");
  return { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) };
}

const EMPTY = {
  name: "", description: "", category: "", town: "Ayr", venue: "", address: "",
  eventDate: "", eventTime: "", price: "", imageUrl: "", bookingLink: "",
  organiserName: "", organiserContact: "",
};

interface EventItem {
  id: number; name: string; category: string; town: string; venue: string | null;
  eventDate: string; eventTime: string | null; price: string | null;
  description: string; imageUrl: string | null; bookingLink: string | null;
  organiserName: string | null; organiserContact: string | null; address: string | null;
  approved: boolean; featured: boolean; sponsored: boolean; createdAt: string;
  status?: string; rejectionReason?: string | null;
}

export default function AdminEvents() {
  const { user, isLoading: authLoading } = useAuth();
  const { toast } = useToast();
  const [items, setItems] = useState<EventItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [townFilter, setTownFilter] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState<EventItem | null>(null);
  const [form, setForm] = useState({ ...EMPTY });
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const eventCategories = useCategories("event");
  useEffect(() => {
    if (!form.category && eventCategories.length > 0) setForm(f => ({ ...f, category: eventCategories[0]! }));
  }, [eventCategories]);

  if (authLoading) return null;
  if (user?.role !== "admin") return <Redirect to="/" />;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch("/api/admin/events/all", { headers: authHeaders() });
      const data = await res.json() as { items: EventItem[] };
      setItems(data.items ?? []);
    } catch {
      toast({ title: "Failed to load events", variant: "destructive" });
    } finally { setLoading(false); }
  }, [toast]);

  useEffect(() => { void load(); }, [load]);

  const set = (k: string, v: string) => setForm(p => ({ ...p, [k]: v }));
  const f = (k: keyof typeof EMPTY) => form[k];

  const openEdit = (item: EventItem) => {
    setEditing(item);
    setShowCreate(false);
    setForm({
      name: item.name, description: item.description, category: item.category,
      town: item.town, venue: item.venue ?? "", address: item.address ?? "",
      eventDate: item.eventDate, eventTime: item.eventTime ?? "",
      price: item.price ?? "", imageUrl: item.imageUrl ?? "",
      bookingLink: item.bookingLink ?? "",
      organiserName: item.organiserName ?? "", organiserContact: item.organiserContact ?? "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleCreate = async () => {
    if (!form.name || !form.description || !form.eventDate) {
      toast({ title: "Name, description and date are required", variant: "destructive" }); return;
    }
    setSaving(true);
    try {
      const r = await fetch("/api/admin/events", {
        method: "POST", headers: authHeaders(),
        body: JSON.stringify({
          name: form.name, description: form.description, category: form.category,
          town: form.town, venue: form.venue || null, address: form.address || null,
          eventDate: form.eventDate, eventTime: form.eventTime || null,
          price: form.price || null, imageUrl: form.imageUrl || null,
          bookingLink: form.bookingLink || null,
          organiserName: form.organiserName || null, organiserContact: form.organiserContact || null,
        }),
      });
      if (!r.ok) { const d = await r.json() as { error?: string }; throw new Error(d.error ?? "Failed"); }
      toast({ title: "Event created and approved" });
      setForm({ ...EMPTY }); setShowCreate(false);
      void load();
    } catch (e) {
      toast({ title: e instanceof Error ? e.message : "Failed to create", variant: "destructive" });
    } finally { setSaving(false); }
  };

  const handleEdit = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      const r = await fetch(`/api/events/${editing.id}`, {
        method: "PUT", headers: authHeaders(),
        body: JSON.stringify({
          name: form.name, description: form.description, category: form.category,
          town: form.town, venue: form.venue || null, address: form.address || null,
          eventDate: form.eventDate, eventTime: form.eventTime || null,
          price: form.price || null, imageUrl: form.imageUrl || null,
          bookingLink: form.bookingLink || null,
          organiserName: form.organiserName || null, organiserContact: form.organiserContact || null,
        }),
      });
      if (!r.ok) throw new Error("Failed");
      toast({ title: "Event updated" });
      setEditing(null); setForm({ ...EMPTY });
      void load();
    } catch {
      toast({ title: "Failed to save", variant: "destructive" });
    } finally { setSaving(false); }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm("Delete this event? This cannot be undone.")) return;
    setDeletingId(id);
    try {
      await fetch(`/api/events/${id}`, { method: "DELETE", headers: authHeaders() });
      setItems(prev => prev.filter(i => i.id !== id));
      toast({ title: "Event deleted" });
    } catch {
      toast({ title: "Failed to delete", variant: "destructive" });
    } finally { setDeletingId(null); }
  };

  const [rejectingId, setRejectingId] = useState<number | null>(null);
  const [rejectReason, setRejectReason] = useState("");

  const approveEvent = async (item: EventItem) => {
    try {
      await fetch(`/api/events/${item.id}/status`, {
        method: "PATCH", headers: authHeaders(),
        body: JSON.stringify({ status: "approved" }),
      });
      setItems(prev => prev.map(i => i.id === item.id ? { ...i, approved: true, status: "approved" } : i));
      toast({ title: `"${item.name}" approved` });
    } catch {
      toast({ title: "Failed to approve", variant: "destructive" });
    }
  };

  const rejectEvent = async (item: EventItem, reason: string) => {
    try {
      await fetch(`/api/events/${item.id}/status`, {
        method: "PATCH", headers: authHeaders(),
        body: JSON.stringify({ status: "rejected", rejectionReason: reason }),
      });
      setItems(prev => prev.map(i => i.id === item.id ? { ...i, approved: false, status: "rejected", rejectionReason: reason } : i));
      setRejectingId(null);
      setRejectReason("");
      toast({ title: `"${item.name}" rejected` });
    } catch {
      toast({ title: "Failed to reject", variant: "destructive" });
    }
  };

  const toggleApproved = async (item: EventItem) => {
    if (item.approved) {
      await rejectEvent(item, "");
    } else {
      await approveEvent(item);
    }
  };

  const pendingItems = items.filter(i => !i.approved && i.status !== "rejected");

  const townOptions = [...new Set(items.map(i => i.town).filter(Boolean))].sort();

  const filtered = items.filter(i => {
    const s = search.trim().toLowerCase();
    if (s && !i.name.toLowerCase().includes(s) && !i.town.toLowerCase().includes(s) && !(i.venue ?? "").toLowerCase().includes(s)) return false;
    if (townFilter && i.town !== townFilter) return false;
    if (fromDate && i.eventDate < fromDate) return false;
    if (toDate && i.eventDate > toDate) return false;
    return true;
  });

  const isFormOpen = showCreate || editing !== null;

  return (
    <AdminLayout current="/admin/events">
      <div className="space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-primary">Events</h1>
            <p className="text-sm text-slate-500">{items.length} total · {items.filter(i => !i.approved).length} pending</p>
          </div>
          {!isFormOpen && (
            <div className="flex items-center gap-2">
              <Link
                href="/admin/venue-review"
                className="flex items-center gap-1.5 px-3 py-2.5 text-sm font-semibold text-slate-600 border border-slate-200 rounded-xl hover:border-primary/40 hover:text-primary transition-colors"
              >
                <GitMerge size={15} /> Review &amp; merge
              </Link>
              <button
                onClick={() => { setShowCreate(true); setEditing(null); setForm({ ...EMPTY }); }}
                className="flex items-center gap-2 bg-primary text-white font-bold px-4 py-2.5 rounded-xl text-sm hover:bg-primary/90 transition-colors"
              >
                <Plus size={16} /> Add Event
              </button>
            </div>
          )}
        </div>

        {/* Moderation queue */}
        {pendingItems.length > 0 && !isFormOpen && (
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 rounded-2xl p-4 space-y-3">
            <div className="flex items-center gap-2">
              <XCircle size={16} className="text-amber-600" />
              <h2 className="font-bold text-amber-800 dark:text-amber-300 text-sm">
                {pendingItems.length} event{pendingItems.length !== 1 ? "s" : ""} awaiting moderation
              </h2>
            </div>
            {pendingItems.map(item => (
              <div key={item.id} className="bg-white rounded-xl border border-amber-100 p-3 flex items-start gap-3">
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-slate-800 truncate">{item.name}</div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {item.eventDate} · {item.town} · {item.venue ?? "No venue"}
                  </div>
                  {item.organiserName && (
                    <div className="text-xs text-slate-400 mt-0.5">By {item.organiserName}</div>
                  )}
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  {rejectingId === item.id ? (
                    <div className="flex gap-1 items-center">
                      <input
                        autoFocus
                        value={rejectReason}
                        onChange={e => setRejectReason(e.target.value)}
                        placeholder="Reason (optional)"
                        className="text-xs border border-slate-200 rounded px-2 py-1 w-36 outline-none"
                        onKeyDown={e => { if (e.key === "Enter") void rejectEvent(item, rejectReason); }}
                      />
                      <button
                        onClick={() => void rejectEvent(item, rejectReason)}
                        className="text-xs font-semibold text-red-600 bg-red-50 px-2 py-1 rounded hover:bg-red-100 transition-colors"
                      >
                        Confirm
                      </button>
                      <button
                        onClick={() => { setRejectingId(null); setRejectReason(""); }}
                        className="text-xs text-slate-500 px-2 py-1 rounded hover:bg-slate-100 transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <>
                      <button
                        onClick={() => void approveEvent(item)}
                        className="text-xs font-semibold text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-lg border border-emerald-200 hover:bg-emerald-100 transition-colors"
                      >
                        ✓ Approve
                      </button>
                      <button
                        onClick={() => setRejectingId(item.id)}
                        className="text-xs font-semibold text-red-700 bg-red-50 px-2.5 py-1 rounded-lg border border-red-200 hover:bg-red-100 transition-colors"
                      >
                        ✗ Reject
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Create / Edit form */}
        {isFormOpen && (
          <Card>
            <CardContent className="pt-5 space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="font-bold text-primary text-lg">{editing ? "Edit Event" : "New Event"}</h2>
                <button onClick={() => { setShowCreate(false); setEditing(null); setForm({ ...EMPTY }); }} className="text-slate-400 hover:text-slate-600 text-xl font-bold">×</button>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <label className="block text-xs font-semibold text-slate-500 mb-1">Event Name *</label>
                  <Input value={f("name")} onChange={e => set("name", e.target.value)} placeholder="e.g. Ayr Summer Festival" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-500 mb-1">Category</label>
                  <select value={f("category")} onChange={e => set("category", e.target.value)} className="w-full h-10 text-sm border border-input rounded-md px-3 bg-background">
                    {eventCategories.map(c => <option key={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-500 mb-1">Town</label>
                  <select value={f("town")} onChange={e => set("town", e.target.value)} className="w-full h-10 text-sm border border-input rounded-md px-3 bg-background">
                    {TOWNS.map(t => <option key={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-500 mb-1">Event Date *</label>
                  <Input type="date" value={f("eventDate")} onChange={e => set("eventDate", e.target.value)} />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-500 mb-1">Event Time</label>
                  <Input type="time" value={f("eventTime")} onChange={e => set("eventTime", e.target.value)} />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-500 mb-1">Venue</label>
                  <Input value={f("venue")} onChange={e => set("venue", e.target.value)} placeholder="e.g. Town Hall, Ayr" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-500 mb-1">Price</label>
                  <Input value={f("price")} onChange={e => set("price", e.target.value)} placeholder="Free, £5, £10–£20" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-500 mb-1">Organiser Name</label>
                  <Input value={f("organiserName")} onChange={e => set("organiserName", e.target.value)} />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-500 mb-1">Organiser Contact</label>
                  <Input value={f("organiserContact")} onChange={e => set("organiserContact", e.target.value)} placeholder="Phone or email" />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-xs font-semibold text-slate-500 mb-1">Address</label>
                  <Input value={f("address")} onChange={e => set("address", e.target.value)} />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-xs font-semibold text-slate-500 mb-1">Booking / Tickets Link</label>
                  <Input value={f("bookingLink")} onChange={e => set("bookingLink", e.target.value)} placeholder="https://…" />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-xs font-semibold text-slate-500 mb-1">Image</label>
                  <ImageUpload value={f("imageUrl")} onChange={url => set("imageUrl", url)} stockCategory="event" />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-xs font-semibold text-slate-500 mb-1">Description *</label>
                  <Textarea value={f("description")} onChange={e => set("description", e.target.value)} rows={4} />
                </div>
              </div>
              <div className="flex gap-3 pt-2 border-t border-slate-100">
                <Button disabled={saving} onClick={editing ? handleEdit : handleCreate} className="bg-primary hover:bg-primary/90">
                  {saving ? "Saving…" : editing ? "Save Changes" : "Create Event"}
                </Button>
                <Button variant="outline" onClick={() => { setShowCreate(false); setEditing(null); setForm({ ...EMPTY }); }}>Cancel</Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Search + filters */}
        <div className="flex flex-wrap items-center gap-2">
          <input
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search by name, town or venue…"
            className="flex-1 min-w-[200px] max-w-sm border border-slate-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-primary/40"
          />
          <select
            value={townFilter}
            onChange={e => setTownFilter(e.target.value)}
            className="h-[42px] border border-slate-200 rounded-xl px-3 text-sm bg-white outline-none focus:border-primary/40"
          >
            <option value="">All towns</option>
            {townOptions.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          <div className="flex items-center gap-1.5">
            <label className="text-xs text-slate-500">From</label>
            <input
              type="date" value={fromDate} onChange={e => setFromDate(e.target.value)}
              className="h-[42px] border border-slate-200 rounded-xl px-3 text-sm outline-none focus:border-primary/40"
            />
            <label className="text-xs text-slate-500">To</label>
            <input
              type="date" value={toDate} onChange={e => setToDate(e.target.value)}
              className="h-[42px] border border-slate-200 rounded-xl px-3 text-sm outline-none focus:border-primary/40"
            />
          </div>
          {(search || townFilter || fromDate || toDate) && (
            <button
              onClick={() => { setSearch(""); setTownFilter(""); setFromDate(""); setToDate(""); }}
              className="text-xs text-slate-400 hover:text-slate-600 px-2"
            >
              Clear
            </button>
          )}
          <span className="text-xs text-slate-400 ml-auto">Showing {filtered.length} of {items.length}</span>
        </div>

        {/* List */}
        {loading ? (
          <div className="text-slate-400 text-sm">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="text-slate-400 text-sm">No events found.</div>
        ) : (
          <div className="space-y-2">
            {filtered.map(item => (
              <div key={item.id} className="bg-white border border-slate-200 rounded-xl px-5 py-4 flex gap-4 items-start">
                {item.imageUrl && <img src={item.imageUrl} alt="" className="w-16 h-16 rounded-lg object-cover flex-shrink-0" />}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start gap-2 flex-wrap">
                    <span className="font-semibold text-slate-800 text-sm leading-tight">{item.name}</span>
                    {item.featured && <span className="text-xs font-bold text-amber-600 bg-amber-50 dark:bg-amber-900/20 px-2 py-0.5 rounded-full flex items-center gap-1"><Star size={10} /> Featured</span>}
                    {item.sponsored && <span className="text-xs font-bold text-purple-600 bg-purple-50 px-2 py-0.5 rounded-full">Boosted</span>}
                    {!item.approved && <span className="text-xs font-bold text-orange-600 bg-orange-50 px-2 py-0.5 rounded-full">Pending</span>}
                  </div>
                  <div className="flex items-center gap-2 mt-1 flex-wrap text-xs text-slate-400">
                    <span>{item.town}</span><span>·</span><span>{item.category}</span><span>·</span>
                    <span>{item.eventDate}</span>
                    {item.venue && <><span>·</span><span>{item.venue}</span></>}
                  </div>
                  <p className="text-xs text-slate-500 mt-1 line-clamp-1">{item.description}</p>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <button onClick={() => void toggleApproved(item)} title={item.approved ? "Approved — click to unpublish" : "Pending — click to approve"}
                    className={`p-1.5 rounded-lg transition-colors ${item.approved ? "text-emerald-600 hover:bg-emerald-50" : "text-slate-400 hover:bg-slate-100"}`}>
                    {item.approved ? <CheckCircle size={18} /> : <XCircle size={18} />}
                  </button>
                  <button onClick={() => openEdit(item)} className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100 transition-colors" title="Edit">
                    <Pencil size={16} />
                  </button>
                  <button onClick={() => void handleDelete(item.id)} disabled={deletingId === item.id}
                    className="p-1.5 rounded-lg text-red-400 hover:bg-red-50 transition-colors disabled:opacity-50" title="Delete">
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
